import { Injectable } from '@angular/core';
import QRCodeStyling, { Options } from 'qr-code-styling';

@Injectable({
  providedIn: 'root'
})
export class QrcodeService {

  private static readonly IMAGE_URL = '/assets/images/black_faviconMatwins.svg';

  // L'image est chargée une seule fois et convertie en data URI pour éviter
  // une requête HTTP par instance QRCodeStyling créée.
  private imageDataUri: string = QrcodeService.IMAGE_URL;
  private imageDataUriPromise: Promise<string>;

  constructor() {
    this.imageDataUriPromise = this.loadImageDataUri();
  }

  private loadImageDataUri(): Promise<string> {
    return fetch(QrcodeService.IMAGE_URL)
      .then(response => {
        if (!response.ok) throw new Error(`Failed to load ${QrcodeService.IMAGE_URL}`);
        return response.blob();
      })
      .then(blob => new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result as string);
        reader.onerror = () => reject(reader.error);
        reader.readAsDataURL(blob);
      }))
      .then(dataUri => this.imageDataUri = dataUri)
      .catch(() => this.imageDataUri); // fallback : URL servie par le navigateur
  }

  private defaultConfig: Partial<Options> = {
    width: 150,
    height: 150,
    type: 'svg',
    margin: 0,
    data: 'https://example.com',
    dotsOptions: {
      color: 'black',
      type: 'square'
    },
    cornersSquareOptions: {
      color: 'black',
      type: 'square'
    },
    cornersDotOptions: {
      color: 'black',
      type: 'dot'
    },
    backgroundOptions: {
      color: 'transparent'//'#ffffff'
    },
    imageOptions: {
      crossOrigin: 'anonymous',
      margin: 2,
      imageSize: 0.38,
      hideBackgroundDots: true
    }
  };

  private createInstance(customData: string, overrideConfig: Partial<Options> = {}): QRCodeStyling {
    const mergedConfig: Options = {
      ...this.defaultConfig,
      data: customData,
      image: overrideConfig.image ?? this.imageDataUri,
      ...overrideConfig
    } as Options;
    return new QRCodeStyling(mergedConfig);
  }

  async getSvgString(data: string, overrideConfig: Partial<Options> = {}): Promise<string> {
    await this.imageDataUriPromise;
    const qr = this.createInstance(data, { ...overrideConfig, type: 'svg' });
    const blob = await qr.getRawData('svg');
    if (!blob) return '';
    if (blob instanceof Blob) return await blob.text();
    return blob.toString();
  }

  async getJpegBlob(data: string, overrideConfig: Partial<Options> = {}): Promise<Blob> {
    await this.imageDataUriPromise;
    const qr = this.createInstance(data, { ...overrideConfig, type: 'canvas' });
    const result = await qr.getRawData('jpeg');
    if (!result) return new Blob();
    if (result instanceof Blob) return result;
    // Convert Buffer to Blob avec cast explicite
    return new Blob([new Uint8Array(result.buffer, result.byteOffset, result.byteLength) as unknown as BlobPart]);
  }

  async getPngBlob(data: string, overrideConfig: Partial<Options> = {}): Promise<Blob> {
    await this.imageDataUriPromise;
    const qr = this.createInstance(data, { ...overrideConfig, type: 'canvas' });
    const result = await qr.getRawData('png');
    if (!result) return new Blob();
    if (result instanceof Blob) return result;
    // Convert Buffer to Blob avec cast explicite
    return new Blob([new Uint8Array(result.buffer, result.byteOffset, result.byteLength) as unknown as BlobPart]);
  }

  async download(
    data: string,
    fileName: string = 'qrcode',
    format: 'svg' | 'jpeg' | 'png' = 'svg',
    overrideConfig: Partial<Options> = {}
  ): Promise<void> {
    await this.imageDataUriPromise;
    const qr = this.createInstance(data, {
      ...overrideConfig,
      type: format === 'svg' ? 'svg' : 'canvas'
    });

    await qr.download({
      name: fileName,
      extension: format as 'svg' | 'jpeg' | 'png'
    });
  }

  // Méthodes spécifiques pour téléchargement direct
  async downloadJpeg(data: string, fileName: string = 'qrcode'): Promise<void> {
    await this.download(data, fileName, 'jpeg');
  }

  async downloadPng(data: string, fileName: string = 'qrcode'): Promise<void> {
    await this.download(data, fileName, 'png');
  }

  appendToElement(
    element: HTMLElement,
    data: string,
    overrideConfig: Partial<Options> = {}
  ): QRCodeStyling {
    const imageAtCreation = this.imageDataUri;
    const qr = this.createInstance(data, overrideConfig);
    qr.append(element);
    // Si l'image n'était pas encore chargée en data URI, on met à jour l'instance
    // une fois la promesse résolue pour éviter le rechargement via l'URL.
    if (!overrideConfig.image) {
      void this.imageDataUriPromise.then(uri => {
        if (uri !== imageAtCreation) qr.update({ image: uri });
      });
    }
    return qr;
  }
}